import * as amplitudeEngagement from '@amplitude/engagement-browser';
import { sendAmplitudeEventWithoutForwarding, waitForAmplitudeDeviceId } from './amplitudeServiceJS';
import { isNil, omitBy } from 'lodash';
import { getAmplitudeEngagementLanguage } from '../../common/analytics/amplitudeLocalizationUtils';

type SyncEngagementArgs = {
    isEnabled: boolean;
    isGuest: boolean;
    userId: string | null;
    locale: string;
    config: {
        analytics?: {
            amplitudeApiKey?: string;
        };
    };
    userProperties?: Record<string, unknown>;
};

type InitialiseEngagementArgs = {
    config: {
        analytics?: {
            amplitudeApiKey?: string;
        };
    };
    locale?: string | null;
    router?: (url: string) => void;
};

const engagementState = {
    isInitialised: false,
    isRouterAttached: false,
    isBooted: false,
    currentLocale: '',
    currentIdentityKey: '',
    syncRunId: 0,
};

/**
 * Setup the engagement SDK
 */
const initialiseEngagement = async ({ config, locale, router }: InitialiseEngagementArgs): Promise<boolean> => {
    const apiKey = config?.analytics?.amplitudeApiKey;
    if (!apiKey) return false;

    const engagementLanguage = locale ? getAmplitudeEngagementLanguage(locale) : '';

    if (!engagementState.isInitialised) {
        amplitudeEngagement.init(apiKey, {
            locale: engagementLanguage || undefined,
            autoRefreshIntervalSeconds: 3600,
        });

        engagementState.isInitialised = true;
        engagementState.currentLocale = engagementLanguage;
    }

    if (router && !engagementState.isRouterAttached) {
        window.engagement?.setRouter(router);
        engagementState.isRouterAttached = true;
    }

    // If the user's language has changed, update the engagement language as well
    if (engagementLanguage && engagementLanguage !== engagementState.currentLocale) {
        await window.engagement?.updateLanguage(engagementLanguage);
        engagementState.currentLocale = engagementLanguage;
    }

    return true;
};

/**
 * Deactivate the engagement service for the current user/device
 */
const shutdownEngagement = () => {
    engagementState.syncRunId += 1;
    window.engagement?.shutdown?.();
    engagementState.isBooted = false;
    engagementState.currentIdentityKey = '';
};

/**
 * Activate the engagement service for the current user/device
 */
const bootEngagement = async ({
    userId,
    deviceId,
    userProperties,
}: {
    userId: string | null;
    deviceId: string | null;
    userProperties?: Record<string, unknown>;
}): Promise<boolean> => {
    if (!userId && !deviceId) return false;

    const user = omitBy(
        {
            user_id: userId,
            device_id: deviceId,
            user_properties: userProperties,
        },
        isNil,
    );

    if (!user) return false;

    await window.engagement?.boot({
        user,
        integrations: [
            {
                track: (event) => {
                    sendAmplitudeEventWithoutForwarding({
                        eventType: event.event_type,
                        eventProperties: event.event_properties,
                        userProperties: event.user_properties,
                    });
                },
            },
        ],
    });

    return true;
};

export const syncEngagementTracking = async ({
    isEnabled: enabled,
    isGuest,
    userId,
    locale,
    config,
    userProperties,
}: SyncEngagementArgs) => {
    const syncRunId = ++engagementState.syncRunId;
    const isStale = () => syncRunId !== engagementState.syncRunId;

    if (!enabled) {
        shutdownEngagement();
        return;
    }

    const initialised = await initialiseEngagement({ config, locale });
    if (isStale()) return;

    if (!initialised) {
        shutdownEngagement();
        return;
    }

    const deviceId = isGuest ? await waitForAmplitudeDeviceId() : null;
    if (isStale()) return;

    const identityKey = !isGuest && userId ? `user:${userId}` : deviceId ? `guest:${deviceId}` : null;
    if (!identityKey) {
        shutdownEngagement();
        return;
    }

    // Shutdown the engagement if the user has changed (eg logout/login)
    if (engagementState.isBooted && engagementState.currentIdentityKey !== identityKey) shutdownEngagement();
    if (engagementState.isBooted && engagementState.currentIdentityKey === identityKey) return;

    const booted = await bootEngagement({
        userId: !isGuest ? userId : null,
        deviceId,
        userProperties,
    });
    if (isStale() || !booted) return;

    engagementState.isBooted = true;
    engagementState.currentIdentityKey = identityKey;
};
